import React, { useState, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import { TherapistRecordingView } from '../therapistRecordings/TherapistRecordingView';
import { RefreshIconButton } from '@/components/util/RefreshIconButton';
import { GenericRelativeDateDisplay } from '@/components/GenericDateFunctionField';
import { RecordingStatusSemaphore } from './RecordingStatusSemaphore';
import { useInlineMemo } from '@/util/useMemoStyles';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { ForeignEntitySystemSnapshot } from '../therapists/TherapistsCommandPanel';
import { TranscribeAllPendingButton } from '../therapistRecordings/TranscribeAllPendingButton';

interface TherapistRecordingsAccordionProps {
  sessionNote: any;
  recordings: any[];
  isLoading: boolean;
  isFetching: boolean;
  refetch: () => void;
}

export const TherapistRecordingsAccordion: React.FC<
  TherapistRecordingsAccordionProps
> = ({ sessionNote, recordings, isLoading, isFetching, refetch }) => {
  const [expanded, setExpanded] = useState(false);
  const [viewTranscribed, setViewTranscribed] = useState(false);

  const onToggleViewTranscribed = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setViewTranscribed(prev => !prev);
    },
    [],
  );

  const handleAccordionChange = useCallback(
    (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded);
    },
    [],
  );

  const onReloadRecordings = useCallback(() => refetch(), [refetch]);

  const lastUpdatedAt = React.useMemo(() => {
    const [firstRecording] = recordings || [];
    return firstRecording?.lastUpdatedAt || firstRecording?.createdAt;
  }, [recordings]);

  const sortedRecordings = useMemo(
    () =>
      recordings
        .filter((rec: any) => {
          if (viewTranscribed) {
            return true;
          }

          return rec.status !== 'transcribed';
        })
        .sort((a: any, b: any) => b.fileIndex - a.fileIndex),
    [recordings, viewTranscribed],
  );

  const lastUpdatedAtChipProps = React.useMemo(
    () => ({
      icon: <HistoryIcon />,
    }),
    [],
  );

  return (
    <>
      <Accordion
        expanded={expanded}
        onChange={handleAccordionChange}
        sx={useInlineMemo({
          marginTop: 2,
          '&.MuiAccordion-root': {
            boxShadow: 'none',
            '&:before': {
              display: 'none',
            },
          },
          '& .MuiAccordionSummary-root': {
            padding: '0 8px 0 0',
            minHeight: 'auto',
            flexDirection: 'row-reverse',
          },
          '& .MuiAccordionSummary-expandIconWrapper': {
            marginRight: 1,
          },
          '& .MuiAccordionDetails-root': {
            padding: '8px 0 16px',
          },
        })}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="recordings-content"
          id="recordings-header"
          sx={useInlineMemo({
            '& .MuiAccordionSummary-content': {
              margin: '12px 0',
              alignItems: 'center',
            },
          })}
        >
          <Typography
            marginLeft="1rem"
            variant="h5"
            component="h2"
            sx={useInlineMemo({ fontWeight: 500 })}
          >
            Recordings
          </Typography>

          <RefreshIconButton
            isFetching={isFetching}
            onClick={onReloadRecordings}
          />

          <Box display="flex" ml={2} alignItems="center">
            <RecordingStatusSemaphore recordings={recordings || []} />

            {lastUpdatedAt && (
              <Box
                marginLeft=".5rem"
                onClick={e => e.stopPropagation()}
              >
                <GenericRelativeDateDisplay
                  chipProps={lastUpdatedAtChipProps}
                  date={lastUpdatedAt}
                />
              </Box>
            )}

            <Box
              marginLeft={1}
              display="inline-flex"
              alignItems="center"
              onClick={e => e.stopPropagation()}
            >
              <TranscribeAllPendingButton
                sessionNote={sessionNote}
                recordings={recordings || []}
              />
            </Box>
          </Box>

          {expanded && (
            <Tooltip
              title={
                viewTranscribed
                  ? 'Hide Transcribed'
                  : 'Show Transcribed'
              }
            >
              <IconButton
                sx={{ marginLeft: '1rem' }}
                onClick={onToggleViewTranscribed}
              >
                {viewTranscribed ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </Tooltip>
          )}
        </AccordionSummary>

        <AccordionDetails>
          {sortedRecordings
            ?.filter(rec => !!rec)
            .map(recording => (
              <TherapistRecordingView
                key={recording.id}
                recording={recording}
              />
            ))}

          {(!sortedRecordings || sortedRecordings.length === 0) && (
            <Typography color="textSecondary">
              {!viewTranscribed
                ? 'No Pending Recordings for this Asset'
                : 'No Recordings for this Asset'}
            </Typography>
          )}

          <Box marginTop="2rem">
            <h3>System Snapshot</h3>
            <ForeignEntitySystemSnapshot
              therapistId={sessionNote.therapistId}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    </>
  );
};
